import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableAlertsPointReport,
  RequestReportInterFace,
} from '../../../../interface/reportAlertsPoint.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchAlertsReportPoint,
  updateReportFilters,
  setReportFiltersData,
  fetchListFilters,
  setAlertsPointReportUpdate,
} from '../../../../redux/slices/ReportAlertsPointSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
}

/**
 * AlertsReportTablePoint component
 */
function AlertsReportTablePoint(properties: ReportTableProperties) {
   const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter } = properties;

  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isAlertsPointReportUpdate,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.reportAlertsPoint);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);

  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailableAlertsPointReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableAlertsPointReport, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingPointName' as keyof AvailableAlertsPointReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableAlertsPointReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role="button" tabIndex={0}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'createdAt',
      queryParamsValue: 'createdAt',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" />,
      sortAllow: true,
      columnWidth: 204,
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailableAlertsPointReport,
      sortAllow: true,
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed' as keyof AvailableAlertsPointReport,
      sortAllow: true,
    },
    {
      label: t('basePricingAlertsConsumption'),
      accessorKey: 'basePricePowerConsumed' as keyof AvailableAlertsPointReport,
      sortAllow: true,
    },

    {
      label: 'peak pricing Alerts Consumption',
      accessorKey: 'peakPricePowerConsumed' as keyof AvailableAlertsPointReport,
      sortAllow: true,
    },
    {
      label: t('offPeakPricingAlertsConsumption'),
      accessorKey: 'offPeakPricePowerConsumed' as keyof AvailableAlertsPointReport,
      sortAllow: true,
    },
    {
      label: t('scheduleChargeAlertsConsumption'),
      accessorKey: 'scheduledPowerConsumed' as keyof AvailableAlertsPointReport,
      sortAllow: true,
    },
  ];
  /**
   * Fetches energy report data for the selected facility.
   * @param data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchAlertsReportPoint({
          ...data,
        }),
      );
    }
  };

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page,
      limit,
      accountCustomId: reportsData?.accountId,
    });
  };

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const exportParameters = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    };
    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }
    reportApi
      .fetchAlertPointListExport({ ...exportParameters, accountCustomId: reportsData?.accountId })
      .then((response) => {
        // Convert data to CSV format
        const headers = [
        t('date'),
          t('stationId'),
          t('stationName'),
          t('powerConsumed'),
          t('revenueGenerated'),
        ];
        const rows = response.data.map((row: AvailableAlertsPointReport) => [
          row.createdAt ? ConvertUTCtoDate(row.createdAt) : '',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  const fetchReportData = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
      accountCustomId: reportsData?.accountId,
    });
  }, [
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);
  /**
   * Function to handle ApiRequest when page or limit changes
   * @param value
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      accountCustomId: reportsData?.accountId,
    });
  };

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isAlertsPointReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setAlertsPointReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isAlertsPointReportUpdate]);

  useEffect(() => {
    if (
      preferenceFilter?.pageName === 'alertsReport' &&
      filters.length > 1 &&
      reportsData.reportByType !== 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter?.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
        accountCustomId: reportsData?.accountId,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (filters.length <= 1 && reportsData?.accountId) {
      dispatch(fetchListFilters({ accountCustomId: reportsData?.accountId }));
    }
  }, [reportsData?.accountId]);

  return (
    <div>
      <CustomTable
        disablePagination={false}
        loading={loading}
        limit={limit}
        header={column}
        data={reports}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        pageLimitChange={handleApiRequest}
        version="v2"
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportData={reports}
        exportLoader={exportLoader}
        disableHeader={false}
        filters={filters}
        filtersToDispatch={updateReportFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListFilters}
        leftTopContent={leftTopContent}
        ReferenceCurrentPages={ReferenceCurrentPages}
        sizeChangeButtonRequired
      />
    </div>
  );
}

export default AlertsReportTablePoint;
